/**
 * useAgentSessionSummary Hook (BL-1bb921de)
 *
 * Today's session summary per agent, keyed by agent id. Refetched when a session
 * starts or ends (agent-session:started / agent-session:ended) and every minute,
 * so an open session's running total stays current.
 */

import { useState, useEffect, useCallback } from 'react';
import type { Socket } from 'socket.io-client';
import { fetchTodaySummaryByAgent, type SessionSummaryRow } from '../services/agentSessionService';

const REFRESH_INTERVAL_MS = 60000;

export function useAgentSessionSummary(socket?: Socket | null): Record<string, SessionSummaryRow> {
  const [byAgent, setByAgent] = useState<Record<string, SessionSummaryRow>>({});

  const load = useCallback(async () => {
    try {
      const rows = await fetchTodaySummaryByAgent();
      const next: Record<string, SessionSummaryRow> = {};
      for (const row of rows) if (row.key) next[row.key] = row;
      setByAgent(next);
    } catch (err) {
      // The cards still show without session times; the next refresh tries again.
      console.error('Failed to load agent session summary:', err);
    }
  }, []);

  useEffect(() => {
    void load();
    const interval = setInterval(() => void load(), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    if (!socket) return;
    const refresh = (): void => void load();
    socket.on('agent-session:started', refresh);
    socket.on('agent-session:ended', refresh);
    return () => {
      socket.off('agent-session:started', refresh);
      socket.off('agent-session:ended', refresh);
    };
  }, [socket, load]);

  return byAgent;
}
